import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import {
  type AssetType,
  type PartyType,
} from '~/modules/asset-reparsing/types';

import { i18n } from '~/utils';

import { queryKeysAssetReparsing } from '../asset-reparsing/queryKeys';

/**
 * Builds an alias-address mapping payload for the old asset-reparsing flow.
 * This flow has a loaded reparsing asset and reads the party name/address and
 * issuer owner from `asset.assetParties`.
 *
 * @param {string} companyId - The ID of the selected company.
 * @param {string} party - The party from the asset for which the company mapping should be created.
 * @param {Object} asset - The asset object containing the asset parties.
 * @return {Object} The mapped data including name, address, and company ID.
 * @throws {Error} Throws an error if any of the required arguments is missing or invalid.
 */
const mapAddressMappingFE2BE = (
  companyId: UUID,
  party: PartyType,
  asset: AssetType,
) => {
  if (
    !companyId ||
    !party ||
    !asset?.assetParties?.[party] ||
    !asset?.assetParties?.issuer
  ) {
    throw new Error(
      'Invalid arguments: companyId, party, or asset is missing or invalid',
    );
  }

  const { address, name } = asset.assetParties[party].legalOrganization;

  const data = {
    address,
    companyId,
    issuerId: asset.assetParties.issuer.id,
    name,
  };

  return data;
};

/**
 * @see https://app.dev.vestigas.com/redoc#tag/Company-Addresses/operation/create_company_addresses_company_address_post
 */
export const createCompanyAddressMapping = async (
  body: ReturnType<typeof mapAddressMappingFE2BE>,
) => {
  try {
    // Hits the deprecated `company_address` endpoint; the response body is
    // never read here, so no response schema is declared.
    return await vestigasApi.post(ENDPOINT.COMPANY.ADDRESS.CREATE(), {
      json: body,
    });
  } catch (error) {
    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 400: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.mapCompanyAddress.error.400',
          );
          break;
        }

        case 409: {
          error.message = i18n.t(
            'assetReparsing.mutation.mapCompanyAddress.error.409',
          );
          break;
        }

        case 422: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.mapCompanyAddress.error.422',
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.mapCompanyAddress.error.generic',
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * Maps an unmatchable party from a loaded asset-reparsing asset to the company
 * selected by the user. Use the DNPv2 parsing-case hook when the source data is
 * `dln_data` instead of `asset.assetParties`.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company-Addresses/operation/create_company_addresses_company_address_post
 */
export const useMutationMapCompanyAddress = (
  asset: AssetType,
  options?: UseMutationOptions,
): UseMutationResult => {
  const queryClient = useQueryClient();

  // @ts-expect-error // TODO: figure out how to correctly work with types of useMutation
  return useMutation({
    // @ts-expect-error // TODO: figure out how to correctly work with types of useMutation
    mutationFn({ companyId, party }: { companyId: string; party: PartyType }) {
      const body = [mapAddressMappingFE2BE(companyId, party, asset)];
      createCompanyAddressMapping(body);
    },
    onError(error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: queryKeysAssetReparsing.getAll(),
      });
    },
    onSuccess() {
      ToastService.success(
        i18n.t(($) => $.assetReparsing.mutation.mapCompanyAddress.success),
      );
    },
    ...options,
  });
};
